<template>
  <div class="relative">
    <div class="mb-4 rounded-md bg-white px-4 py-1">
      <PaginatedList :load="loadDocuments" :query-key="queryKey">
        <template #entry="{ entry: document }">
          <div class="relative border-b">
            <div
              class="relative flex cursor-pointer items-center gap-4 pb-3 pt-3"
              @click="previewDocument(document)"
            >
              <div class="basis-1/2 md:basis-[25%]">{{ document.name }}</div>
              <div class="basis-1/2 md:basis-[20%]">
                <span class="rounded-sm bg-obsidian-100 px-2 py-0.5 text-obsidian-800">
                  {{ t(`workshopAppointmentDocumentTypes.${document.type as string}`) }}
                </span>
              </div>
              <div class="hidden basis-[42%] md:block">
                {{ document.comment ? shortenText(document.comment, 70) : '-' }}
              </div>
              <div class="ml-auto" @click.stop>
                <DropdownDotted :buttons="dropdownButtons(document)" />
              </div>
            </div>
          </div>
        </template>
      </PaginatedList>
    </div>

    <div v-if="!immutable">
      <WorkshopAppointmentDocumentsForm
        v-if="addingDocument"
        :workshop-appointment-id="workshopAppointmentId"
        @complete="addingDocument = false"
      />
      <div v-else class="flex justify-end">
        <CVButton @click.prevent="addDocument">
          {{ t('addDocument') }}
        </CVButton>
      </div>
      <div
        v-if="isRemovingDocument"
        class="absolute inset-[-10px] flex items-center justify-center bg-black bg-opacity-30"
      >
        <Spinner />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import DropdownDotted from '@/components/DropdownDotted.vue';
import PaginatedList from '@/components/PaginatedList.vue';
import CVButton from '@/components/buttons/CVButton.vue';
import Spinner from '@/components/icons/Spinner.vue';
import { usePdfPreview } from '@/hooks/use-pdf-preview';
import { shortenText } from '@/hooks/use-shorten-text';
import { workshopAppointmentDocumentsService } from '@/api/workshop-appointment-documents.service';
import { useRemoveWorkshopAppointmentDocument } from '@/queries/use-workshop-appointment-documents';
import { Alert } from '@/services/alert.service';
import { fetchBlobFromUrl } from '@/utils/download-file';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import WorkshopAppointmentDocumentsForm from './WorkshopAppointmentDocumentsForm.vue';
import type { WorkshopAppointmentDocumentDto } from '@carvia/ros-client-types';

const props = defineProps<{
  workshopAppointmentId: string;
  immutable?: boolean;
}>();

const { t } = useI18n();
const pdfPreview = usePdfPreview();
const addingDocument = ref(false);

const queryKey = computed(() => [
  'workshop-appointment-documents',
  props.workshopAppointmentId,
  {},
]);
const loadDocuments = (page: number) =>
  workshopAppointmentDocumentsService.getAllPaginated(props.workshopAppointmentId, {
    page,
  });

const { mutateAsync: removeDocumentMutation, isPending: isRemovingDocument } =
  useRemoveWorkshopAppointmentDocument();
const removeDocument = async (documentId: string) => {
  const alertResult = await Alert.fire({
    titleText: t('reallyDeleteDocumentTitle'),
    text: t('reallyDeleteDocumentText'),
    icon: 'question',
    showConfirmButton: false,
    showDenyButton: true,
    denyButtonText: t('delete'),
    showCancelButton: true,
    cancelButtonText: t('keep'),
  });

  if (alertResult.isDenied) {
    removeDocumentMutation({ workshopAppointmentId: props.workshopAppointmentId, documentId });
  }
};

const addDocument = () => {
  if (addingDocument.value) return;
  addingDocument.value = true;
};

const downloadDocument = (document: WorkshopAppointmentDocumentDto) => {
  if (!document.documentFileUrl) return;
  window.location.href = document.documentFileUrl;
};

const previewDocument = async (document: WorkshopAppointmentDocumentDto) => {
  if (!document.documentFileUrl) return;
  await pdfPreview.openPdfPreview({
    title: document.name,
    fileName: document.documentFileKey,
    load: () => fetchBlobFromUrl(document.documentFileUrl),
    errorMessage: t('couldNotLoadDocument'),
  });
};

const dropdownButtons = (document: WorkshopAppointmentDocumentDto) => [
  { title: t('preview'), onClick: () => previewDocument(document) },
  { title: t('download'), onClick: () => downloadDocument(document) },
  ...(!props.immutable ? [{ title: t('delete'), onClick: () => removeDocument(document.id) }] : []),
  ...(document.comment
    ? [
        {
          title: t('viewComment'),
          onClick: () =>
            Alert.fire({
              text: document.comment ?? '',
              icon: 'info',
              confirmButtonText: t('close'),
            }),
        },
      ]
    : []),
];
</script>

<i18n lang="json">
{
  "en": {
    "preview": "Preview",
    "download": "Download",
    "addDocument": "+ Add Document",
    "reallyDeleteDocumentTitle": "Really remove Document?",
    "reallyDeleteDocumentText": "The File will be permanently deleted.",
    "keep": "Keep",
    "noDocuments": "This appointment has no documents",
    "viewComment": "View Comment",
    "close": "Close",
    "couldNotLoadDocument": "The document could not be loaded. Please try again.",
    "workshopAppointmentDocumentTypes": {
      "INVOICE": "Invoice",
      "OTHER": "Other"
    }
  },
  "de": {
    "preview": "Vorschau",
    "download": "Download",
    "addDocument": "+ Dokument hinzufügen",
    "reallyDeleteDocumentTitle": "Dokument wirklich entfernen?",
    "reallyDeleteDocumentText": "Die Datei wird unwiderruflich gelöscht.",
    "keep": "Behalten",
    "noDocuments": "Dieser Termin hat noch keine Dokumente",
    "viewComment": "Kommentar anzeigen",
    "close": "Schließen",
    "couldNotLoadDocument": "Das Dokument konnte nicht geladen werden. Bitte versuche es erneut.",
    "workshopAppointmentDocumentTypes": {
      "INVOICE": "Rechnung",
      "OTHER": "Sonstiges"
    }
  }
}
</i18n>
